import React, { useState } from 'react';
import { Product, AllocationStrategy, SolverInfo } from '../../types';

interface PurchaseOrderFormProps {
  products: Product[];
  solverInfo: SolverInfo | null;
  onSubmit: (productId: string, quantity: number, strategy: AllocationStrategy, solverType: 'simple' | 'advanced') => void;
  isLoading: boolean;
  disabled: boolean;
}

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({
  products,
  solverInfo,
  onSubmit,
  isLoading,
  disabled
}) => {
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [strategy, setStrategy] = useState<AllocationStrategy>(AllocationStrategy.SPLIT);
  const [solverType, setSolverType] = useState<'simple' | 'advanced'>('simple');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Set default product and solver when data loads
  React.useEffect(() => {
    if (products.length > 0 && !selectedProductId) {
      setSelectedProductId(products[0].product_id);
    }
    if (solverInfo && solverInfo.default_solver) {
      setSolverType(solverInfo.default_solver);
    }
  }, [products, selectedProductId, solverInfo]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedProductId) {
      newErrors.product = 'Please select a product';
    }

    if (!quantity.trim()) {
      newErrors.quantity = 'Please enter a quantity';
    } else {
      const numQuantity = parseFloat(quantity);
      if (isNaN(numQuantity) || numQuantity <= 0) {
        newErrors.quantity = 'Quantity must be a positive number';
      } else if (numQuantity > 1000000) {
        newErrors.quantity = 'Quantity cannot exceed 1,000,000 units';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const numQuantity = parseFloat(quantity);
    onSubmit(selectedProductId, numQuantity, strategy, solverType);
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuantity(value);
    
    // Clear quantity error when user starts typing
    if (errors.quantity) {
      setErrors(prev => ({ ...prev, quantity: '' }));
    }
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProductId(e.target.value);
    
    // Clear product error when user selects
    if (errors.product) {
      setErrors(prev => ({ ...prev, product: '' }));
    }
  };

  return (
    <div className="purchase-order-form">
      <h3>Purchase Order Details</h3>
      
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="product-select">Product:</label>
          <select
            id="product-select"
            value={selectedProductId}
            onChange={handleProductChange}
            disabled={disabled}
            className={errors.product ? 'error' : ''}
          >
            <option value="">Select a product...</option>
            {products.map(product => (
              <option key={product.product_id} value={product.product_id}>
                {product.name} ({product.unit_of_measure})
              </option>
            ))}
          </select>
          {errors.product && <span className="error-text">{errors.product}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="quantity-input">Purchase Order Quantity:</label>
          <input
            id="quantity-input"
            type="number"
            value={quantity}
            onChange={handleQuantityChange}
            placeholder="Enter quantity..."
            min="0"
            step="1"
            disabled={disabled}
            className={errors.quantity ? 'error' : ''}
          />
          {errors.quantity && <span className="error-text">{errors.quantity}</span>}
        </div>

        <div className="form-group">
          <label>Allocation Strategy:</label>
          <div className="radio-group">
            <div className="radio-option">
              <input
                type="radio"
                id="strategy-split"
                name="strategy"
                value={AllocationStrategy.SPLIT}
                checked={strategy === AllocationStrategy.SPLIT}
                onChange={(e) => setStrategy(e.target.value as AllocationStrategy)}
                disabled={disabled}
              />
              <label htmlFor="strategy-split">
                Split Allocation
              </label>
            </div>
            
            <div className="radio-option">
              <input
                type="radio"
                id="strategy-intact"
                name="strategy"
                value={AllocationStrategy.INTACT}
                checked={strategy === AllocationStrategy.INTACT}
                onChange={(e) => setStrategy(e.target.value as AllocationStrategy)}
                disabled={disabled}
              />
              <label htmlFor="strategy-intact">
                Intact Allocation
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Optimization Solver:</label>
          <div className="radio-group">
            <div className="radio-option">
              <input
                type="radio"
                id="solver-simple"
                name="solver"
                value="simple"
                checked={solverType === 'simple'}
                onChange={(e) => setSolverType(e.target.value as 'simple' | 'advanced')}
                disabled={disabled}
              />
              <label htmlFor="solver-simple">
                Simple Solver
              </label>
            </div>
            
            {solverInfo?.advanced_available && (
              <div className="radio-option">
                <input
                  type="radio"
                  id="solver-advanced"
                  name="solver"
                  value="advanced"
                  checked={solverType === 'advanced'}
                  onChange={(e) => setSolverType(e.target.value as 'simple' | 'advanced')}
                  disabled={disabled}
                />
                <label htmlFor="solver-advanced">
                  Advanced Solver
                </label>
              </div>
            )}
            
            {!solverInfo?.advanced_available && (
              <div className="radio-option disabled">
                <input
                  type="radio"
                  id="solver-advanced"
                  name="solver"
                  value="advanced"
                  disabled
                />
                <label htmlFor="solver-advanced">
                  Advanced Solver (Unavailable)
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={disabled || isLoading}
          >
            {isLoading ? 'Optimizing...' : 'Run Optimization'}
          </button>
          
          {!disabled && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setQuantity('');
                setStrategy(AllocationStrategy.SPLIT);
                setSolverType(solverInfo?.default_solver || 'simple');
                setErrors({});
              }}
            >
              Clear Form
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PurchaseOrderForm;